import {
  ChangeEvent,
  FC,
  forwardRef,
  MouseEvent,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import CallForHelpWinkBox from 'components/call-for-help/CallForHelpWinkBox';
import { Button, Fab } from 'components/core/button';
import { H4, Text } from 'components/core/text/Text';
import { color } from 'components/core/theme';
import { mediaMobile } from 'components/core/theme/breakpoints';
import { FooterChildrenWrapper } from 'components/structure/footer/FooterChildrenWrapper';
import { FooterPortal } from 'components/structure/footer/FooterPortal';
import { useScrollOnMount } from 'hooks/useScrollOnMount';
import { ArrowLeft, X } from 'react-feather';

import { InnerContent } from './styles';

interface Props {
  onGoBack: () => void;
  onSkip: () => void;
  prohibitBackButton: boolean;
  prohibitSkipButton?: boolean;
  onSubmit: (front: File, back: File) => void;
}

const HelpInnerContent = styled(InnerContent)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BlockWrapper = styled.label<{ hasImage?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${color('followupBackground')};
  border: 1px dashed ${color('followupColor')};
  border-radius: 20px;
  max-width: 380px;

  padding: ${props => (!props.hasImage ? '32px 91px' : '0')};
  cursor: pointer;

  .mobile-visible {
    display: none;
  }

  img {
    border-radius: 20px;
  }

  ${mediaMobile} {
    padding: ${props => (!props.hasImage ? '24px 48px' : '0')};

    .mobile-visible {
      display: initial;
    }
    .desktop-visible {
      display: none;
    }
  }
`;

const DeleteButton = styled.div`
  position: absolute;
  left: 40%;
  top: 60%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  svg {
    background-color: ${color('error')};
    color: white;
    width: 24px;
    height: 24px;
    padding: 2px;
    border-radius: 50%;
  }

  .outlined {
    background-color: gray;
    padding: 4px;
    border-radius: 8px;
  }
`;
const FileDiv = styled.div`
  max-width: 380px;
  width: 100%;
`;

interface UploadBlockProps {
  name: string;
  label: string;
  onChange: () => void;
  removeImage: () => void;
}

const UploadBlock = forwardRef<HTMLInputElement, UploadBlockProps>(
  ({ name, label, onChange, removeImage }, ref) => {
    const [img, setImg] = useState<string | null>(null);

    const localOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setImg(URL.createObjectURL(file));
      } else {
        setImg(null);
      }

      onChange();
    };

    const localRemoveImage = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setImg(null);

      removeImage();
    };

    return (
      <BlockWrapper htmlFor={name} hasImage={!!img}>
        {img ? (
          <>
            <img src={img} />{' '}
            <DeleteButton onClick={localRemoveImage}>
              <X color="white" />
              <div>
                <Text variant="subheadline2" color="white" className="outlined">
                  Delete
                </Text>
              </div>
            </DeleteButton>
          </>
        ) : (
          <>
            <Text variant="subheadline2">{label}</Text>
            <div className="desktop-visible flex flex-col items-center my-2">
              <Text color="greenMedium" variant="text3">
                Select a file to upload
              </Text>
              <Text variant="text5">(or drag and drop it here)</Text>
            </div>
            <div className="mobile-visible flex flex-col items-center my-2">
              <Text color="greenMedium" variant="text3">
                Take a picture
              </Text>
            </div>
            <Text variant="text7" className="desktop-visible">
              Supported: JPEG, PNG
            </Text>
          </>
        )}
        <input
          id={name}
          name={name}
          type="file"
          onChange={localOnChange}
          className="hidden"
          accept="image/jpeg,image/png"
          ref={ref}
        />
      </BlockWrapper>
    );
  },
);

export const EligibilityHelpPage: FC<Props> = ({
  onGoBack,
  prohibitBackButton,
  prohibitSkipButton,
  onSkip,
  onSubmit,
}) => {
  useScrollOnMount();
  const frontRef = useRef<HTMLInputElement | null>(null);
  const backRef = useRef<HTMLInputElement | null>(null);

  const [canContinue, setCanContinue] = useState(false);

  const skip = () => {
    onSkip();
  };

  const submit = () => {
    if (!frontRef.current?.files?.[0] || !backRef.current?.files?.[0]) return;

    onSubmit(frontRef.current.files[0], backRef.current.files[0]);
  };

  const onChange = () => {
    setCanContinue(
      (!!frontRef.current?.files?.length && !!backRef.current?.files?.length) ??
        false,
    );
  };

  return (
    <>
      {!prohibitBackButton && (
        <Fab className="-ml-3" aria-label="Back button" onClick={onGoBack}>
          <ArrowLeft />
        </Fab>
      )}
      <HelpInnerContent>
        <H4>Upload Insurance Card</H4>
        <div>
          <Text variant="text4">
            Upload a photo of the front and back of your insurance card, and
            someone from Bend will contact you to help.
          </Text>
        </div>
        {!prohibitSkipButton && (
          <div>
            <Text variant="text4">
              Or, skip this step for now and we will follow up before you begin
              care.
            </Text>
          </div>
        )}
        <div className="flex flex-col items-center gap-4 pt-4">
          <FileDiv>
            <UploadBlock
              name="frontOfCard"
              label="Front of Card"
              onChange={onChange}
              ref={frontRef}
              removeImage={() => {
                if (!frontRef.current) return;
                frontRef.current.value = '';
                onChange();
              }}
            />
          </FileDiv>
          <FileDiv>
            <UploadBlock
              name="backOfCard"
              label="Back of Card"
              onChange={onChange}
              ref={backRef}
              removeImage={() => {
                if (!backRef.current) return;
                backRef.current.value = '';
                onChange();
              }}
            />
          </FileDiv>
        </div>
      </HelpInnerContent>
      <CallForHelpWinkBox />
      <FooterPortal>
        <FooterChildrenWrapper>
          <Button onClick={submit} disabled={!canContinue}>
            Continue
          </Button>
          {!prohibitSkipButton && (
            <Button variant="secondary" onClick={skip}>
              Skip this step
            </Button>
          )}
        </FooterChildrenWrapper>
      </FooterPortal>
    </>
  );
};
